import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { getLangGraphBaseUrl } from '../config/brand.js';

const messageType = {
  answer: 'answer',
  question: 'question',
  form: 'form',
  system: 'system',
};

export const useRichChat = ({
  username,
  selectedModel = 'llama4',
  selectedFlow = null,
  temperature = 0.7,
  playAudio = false,
  onSendMessage,
  onFormSubmit,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [flowSessionId, setFlowSessionId] = useState(null);
  const [flowContext, setFlowContext] = useState(null);

  // Handle form field changes
  const handleFormFieldChange = useCallback((fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  // Handle form submission
  const handleFormSubmit = useCallback(async (formId, data) => {
    try {
      if (onFormSubmit) {
        await onFormSubmit(formId, data);
      }

      // Send form data as a message
      if (onSendMessage) {
        await onSendMessage({
          type: messageType.question,
          content: `Form submitted: ${JSON.stringify(data)}`,
          timestamp: new Date(),
        });
      }

      setFormData({});
      setActiveForm(null);
      toast.success('Form submitted successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    }
  }, [onFormSubmit, onSendMessage]);

  // Handle rich message sending
  const handleSendRichMessage = useCallback(async (content, type = 'text') => {
    if (!content.trim()) return;

    try {
      setIsProcessing(true);

      // Add user message
      if (onSendMessage) {
        await onSendMessage({
          type: messageType.question,
          content: content.trim(),
          timestamp: new Date(),
        });
      }

      // Process with AI
      if (selectedFlow) {
        await processWithFlow(content.trim());
      } else {
        await processWithLLM(content.trim());
      }
    } catch (error) {
      console.error('Message sending error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFlow, selectedModel, temperature, onSendMessage]);

  // Process message with LangGraph flow
  const processWithFlow = useCallback(async (content) => {
    try {
      const pythonBackendUrl = getLangGraphBaseUrl();
      const res = await fetch(`${pythonBackendUrl}/execute-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow: selectedFlow,
          input: { input: content },
          sessionId: flowSessionId,
          context: flowContext,
          user_id: username,
        }),
      });

      const data = await res.json();
      setFlowSessionId(data.sessionId);
      setFlowContext(data.context);

      const executionId = data.execution_id;

      // Poll for status
      let { status } = data;
      let pollResult = null;
      let pollError = null;
      let pollCount = 0;

      while (status === 'pending' || status === 'running') {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        pollCount++;

        const pollRes = await fetch(`${pythonBackendUrl}/flow-status/${executionId}`);
        const pollData = await pollRes.json();
        status = pollData.status;
        pollResult = pollData.result;
        pollError = pollData.error;

        if (pollCount > 30) break; // 30*2s = 60s max
      }

      const responseContent = pollResult?.data || pollResult || pollError || 'No response';

      // Check if response is a form and process accordingly
      let messageToSend;
      try {
        const parsedResponse = JSON.parse(responseContent);
        if (parsedResponse.type === 'form') {
          // Create form message
          messageToSend = {
            type: messageType.form,
            content: parsedResponse.content,
            timestamp: new Date(),
            id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          };
        } else {
          // Regular text response
          messageToSend = {
            type: messageType.answer,
            content: responseContent,
            timestamp: new Date(),
          };
        }
      } catch (e) {
        // Not JSON, treat as regular text
        messageToSend = {
          type: messageType.answer,
          content: responseContent,
          timestamp: new Date(),
        };
      }

      if (onSendMessage) {
        await onSendMessage(messageToSend);
      }

      // Play audio if enabled (only for text responses)
      if (playAudio && messageToSend.type === messageType.answer) {
        speakResponse(messageToSend.content);
      }
    } catch (error) {
      console.error('Flow processing error:', error);
      throw error;
    }
  }, [selectedFlow, flowSessionId, flowContext, username, onSendMessage, playAudio]);

  // Process message with LLM
  const processWithLLM = useCallback(async (content) => {
    try {
      // Import chat API dynamically to avoid circular dependencies
      const { chatCompletion } = await import('../api/chat.api.js');

      const { response, err } = await chatCompletion({
        username,
        prompt: content,
        model: selectedModel,
        temperature,
      });

      if (response) {
        if (onSendMessage) {
          await onSendMessage({
            type: messageType.answer,
            content: response.text || response.message || 'No response',
            timestamp: new Date(),
          });
        }

        // Play audio if enabled
        if (playAudio && response.text) {
          speakResponse(response.text);
        }
      }

      if (err) {
        console.error('LLM error:', err);
        if (onSendMessage) {
          await onSendMessage({
            type: messageType.answer,
            content: err.message || 'Sorry, there was an error processing your request.',
            timestamp: new Date(),
          });
        }
      }
    } catch (error) {
      console.error('LLM processing error:', error);
      throw error;
    }
  }, [username, selectedModel, temperature, onSendMessage, playAudio]);

  // Text-to-speech function
  const speakResponse = useCallback((text) => {
    if (!window.speechSynthesis) {
      toast.error('Speech synthesis not supported.');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      console.log('Speech started');
    };

    utterance.onend = () => {
      console.log('Speech ended');
    };

    utterance.onerror = (error) => {
      console.error('Speech error:', error);
    };

    window.speechSynthesis.speak(utterance);
  }, []);

  // Create form message
  const createFormMessage = useCallback((formConfig) => ({
    type: messageType.form,
    content: formConfig,
    timestamp: new Date(),
    id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  }), []);

  // Create system message
  const createSystemMessage = useCallback((content) => ({
    type: messageType.system,
    content,
    timestamp: new Date(),
    id: `system_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  }), []);

  return {
    isProcessing,
    formData,
    activeForm,
    setActiveForm,
    handleFormFieldChange,
    handleFormSubmit,
    handleSendRichMessage,
    createFormMessage,
    createSystemMessage,
    speakResponse,
  };
};
